import './mail-outgoing-selection-workspace.ajscss';

import { IAgentConfigDeprecatedService } from '@agent/core/agent/agent-config-deprecated.service';
import { IEpticaOutgoingFrame } from '@agent/core/channel-digit/mail/eptica/models/eptica.request.interfaces';
import { IRecipient, RecipientSearchType } from '@agent/core/channel-digit/mail/models/recipient';
import { IOutgoingMailDeprecatedService, OPENED_FOR_CONTACT } from '@agent/core/channel-digit/mail/outgoing-mail-deprecated.service';
import { isEmailValid } from '@agent/core/channel-digit/mail/util/mail-util';
import { IContactDeprecatedService } from '@agent/core/contact/contact-deprecated.service';
import { IContactHistoryService } from '@agent/core/contact/history/contact-history.service';
import { IDirectoryService } from '@agent/core/search/directory.service';
import { IDirectoryContact } from '@agent/core/search/models/directory-contact';
import { SessionDeprecatedService } from '@commons/core/auth/session-deprecated.service';
import { RxUtil } from '@commons/util/utilities/rx';
import { IComponentController, IComponentOptions, ILogService, IPromise, isObject } from 'angular';
import { i18n } from 'i18next';

import { Booking, BookingFacade, capitalize, MediaType } from '@contact/upgrade';

import templateUrl from './mail-outgoing-selection-workspace.template.html';

const STRING_MAX_LENGTH_INPUT: number = 50;

class MailOutgoingSelectionWorkspaceCtrl implements IComponentController {
  responseId: string;
  sourceResponseId: string = '';
  sourceContactId: string = '';
  contactNotFound: boolean = false;
  manualEntryValid: boolean = false;
  itemsPerPage: number = 10;
  currentPage: number = 1;
  maxSize: number = 3;
  totalItems: number = 0;
  repositoryName = '';
  newUri: string;

  request: IEpticaOutgoingFrame;
  private contactBooking: Booking | undefined;

  constructor(
    private readonly $log: ILogService,
    private readonly contactDeprecatedService: IContactDeprecatedService,
    private readonly sessionDeprecatedService: SessionDeprecatedService,
    private readonly outgoingMailService: IOutgoingMailDeprecatedService,
    private readonly $i18next: i18n,
    private readonly agentHistory: IContactHistoryService,
    private readonly agentConfigDeprecatedService: IAgentConfigDeprecatedService,
    private readonly directoryService: IDirectoryService,
    private readonly contactFacade: BookingFacade
  ) {}

  get recipientTypes(): Array<RecipientSearchType> {
    return this.outgoingMailService.getRecipientTypes();
  }

  get selectedRecipientType(): RecipientSearchType {
    return this.outgoingMailService.getSelectedRecipientType();
  }

  set selectedRecipientType(recipientType: RecipientSearchType) {
    this.outgoingMailService.selectRecipientType(recipientType);
  }

  get emailInput(): string {
    return this.outgoingMailService.getEmailInput();
  }

  set emailInput(emailInput) {
    this.outgoingMailService.setEmailInput(emailInput);
  }

  get mode() {
    return this.outgoingMailService.getMode();
  }

  get candidateContactList(): IRecipient[] {
    return this.outgoingMailService.getcandidateContactList();
  }

  get correspondantInput(): string {
    return this.outgoingMailService.getCorrespondantInput();
  }

  set correspondantInput(correspondantInput: string) {
    this.outgoingMailService.setCorrespondantInput(correspondantInput);
  }

  get contactIdInput(): string {
    return this.outgoingMailService.getContactIdInput();
  }

  set contactIdInput(contactIdInput: string) {
    this.outgoingMailService.setContactIdInput(contactIdInput);
  }

  get selectedCorrespondant(): IDirectoryContact | undefined {
    return this.outgoingMailService.getSelectedCorrespondant();
  }

  set selectedCorrespondant(correspondant: IDirectoryContact | undefined) {
    this.outgoingMailService.setSelectedCorrespondant(correspondant);
  }

  get isEpticaFrameLoaded() {
    return this.outgoingMailService.isEpticaFrameLoaded();
  }

  get canPreview() {
    return this.sessionDeprecatedService.hasPermission('CSR::mail_display:preview');
  }

  get canPrint() {
    return this.sessionDeprecatedService.hasPermission('CSR::mail_display:print');
  }

  get emailValid(): boolean {
    return isEmailValid(this.emailInput);
  }

  get selectedContact(): IRecipient | undefined {
    return this.outgoingMailService.getSelectedContact();
  }

  set selectedContact(contact: IRecipient | undefined) {
    this.outgoingMailService.setSelectedContact(contact);
  }

  get actionInProgress(): boolean {
    return this.outgoingMailService.isActionInProgress();
  }

  get formattedName(): string {
    if (this.selectedContact) {
      return this.formatName(this.selectedContact.name);
    }
    return '';
  }

  get contactEmail(): string {
    if (this.selectedContact) {
      return this.selectedContact.email;
    }
    return '';
  }

  $onInit(): void {
    // does the mail outgoing mail request come from another interaction or target a contact ?
    this.contactDeprecatedService
      .getContact(this.responseId)
      .then(contact => {
        if (contact.customerId) {
          this.sourceResponseId = this.outgoingMailService.getOpenedFromInteraction(contact.customerId);
        }
        if (this.sourceResponseId === OPENED_FOR_CONTACT) {
          this.sourceContactId = contact.customerId || '';
        }
        if (this.sourceResponseId && this.sourceResponseId !== '') {
          this.selectedRecipientType = RecipientSearchType.BY_CONTACT_ID;
        }
      })
      .then(() => {
        this.outgoingMailService.initContact(this.responseId).then(() => {
          this.validate();
        });
      });

    const repository$ = RxUtil.fromPromise(() => this.agentHistory.getRepositoryNames(this.sessionDeprecatedService.getProfile().domainId));
    repository$.subscribe(repository => (this.repositoryName = repository));
    this.contactFacade.selected$.subscribe(contact => (this.contactBooking = contact));
  }

  validate(): void {
    let email: string = this.newUri;
    if (this.selectedContact) {
      // if new mail customer, we save the new input mail address
      if (this.outgoingMailService.isNewCustomer(this.selectedContact.id)) {
        email = this.emailInput;
      } else {
        email = this.selectedContact.email;
      }
    } else if (this.emailInput) {
      email = this.emailInput;
    }

    this.outgoingMailService.validateSelection(this.responseId, email, this.contactBooking?.connectionId, this.contactBooking?.agentConnectionId).then(request => {
      this.doPostLoadActions(request);

      this.contactDeprecatedService.getContact(this.responseId).then(contact => {
        if (contact.customerId) {
          this.outgoingMailService.deleteOpenedFromInteraction(contact.customerId);
          this.outgoingMailService.getEpticaRequest().then(epticaRequest => epticaRequest.changeIncomingChannelLabel());
        }
      });
    });
  }

  preview(): void {
    this.outgoingMailService.getEpticaRequest().then(request => request.preview());
  }

  print(): void {
    this.outgoingMailService.getEpticaRequest().then(request => {
      request.focus();
      request.print();
    });
  }

  doPostLoadActions(request: IEpticaOutgoingFrame): void {
    this.request = request;
    request.hideRecipient();
    request.customizeAnswerDisplay();
    request.addPrintAndPreviewButtons();
    const container = document.getElementById('requestFramePlaceHolder');
    if (container) {
      request.show(container);
    }
  }

  onEmailInputChanged(): void {
    if (!!this.emailInput && this.emailInput !== '' && this.emailInput.length < STRING_MAX_LENGTH_INPUT) {
      this.outgoingMailService
        .performSearchContacts(this.repositoryName, this.currentPage, this.itemsPerPage)
        .then(totalItems => {
          this.totalItems = totalItems;
        })
        .catch(() => this.$log.error(`   => Error while retrieving emails with "${this.emailInput}"`));
    } else {
      this.$log.warn('Invalid empty or too long value for email input');
      this.outgoingMailService.resetResults();
    }
  }

  getRecipientTypeLabel(labelKey: RecipientSearchType): string {
    switch (labelKey) {
      case RecipientSearchType.BY_CONTACT_ID:
        return this.$i18next.t('agent:contact.workspace.mailOutgoing.recipientTypeContactId');
      case RecipientSearchType.BY_EMAIL:
        return this.$i18next.t('agent:contact.workspace.mailOutgoing.recipientTypeEmail');
      case RecipientSearchType.BY_DIRECTORY:
        return this.$i18next.t('agent:contact.workspace.mailOutgoing.recipientTypeDirectory');
      case RecipientSearchType.DIRECT:
        return this.$i18next.t('agent:contact.workspace.mailOutgoing.recipientContact');
      default:
        return '';
    }
  }

  onKeyPressCallback(val1): void {
    this.$log.info(`onKeyPressCallback(${val1})`);
  }

  onContactInputChanged(input: string): void {
    this.$log.info(`onContactInputChanged(${input})`);
  }

  onRecipientTypeChange(): void {
    this.$log.info(`onRecipientTypeChange()`);
    this.outgoingMailService.resetResults();
    if (this.selectedRecipientType === 0 && this.emailInput && this.emailInput != null && this.emailInput !== '') {
      this.onEmailInputChanged();
    } else if (this.selectedRecipientType === 1 && this.contactIdInput && this.contactIdInput != null && this.contactIdInput !== '') {
      this.searchContactById();
    }
  }

  isContactSelected(): boolean {
    return this.outgoingMailService.getSelectedContact() != null;
  }

  isValidateButtonActive(): boolean {
    if (this.outgoingMailService.getLock()) {
      return false;
    }
    switch (this.outgoingMailService.getSelectedRecipientType()) {
      case RecipientSearchType.BY_EMAIL:
        return this.isContactSelected() || this.emailValid || this.manualEntryValid;
      case RecipientSearchType.BY_DIRECTORY:
        return this.selectedCorrespondant != null && isObject(this.selectedCorrespondant) && isEmailValid(this.selectedCorrespondant.email);
      case RecipientSearchType.BY_CONTACT_ID:
      case RecipientSearchType.DIRECT:
      default:
        if (this.sourceResponseId !== '') {
          return this.isContactSelected() && this.manualEntryValid;
        }
        return this.isContactSelected();
    }
  }

  isContactButtonDisabled(): boolean {
    return this.contactIdInput == null || this.contactIdInput === '' || this.contactIdInput.length > STRING_MAX_LENGTH_INPUT;
  }

  getCorrespondents(text?: string): IPromise<any> {
    let groupsFilter = this.agentConfigDeprecatedService.domainGroups;
    return this.directoryService
      .getExternalCorrespondents(
        this.sessionDeprecatedService.getProfile().domainId,
        groupsFilter,
        this.agentConfigDeprecatedService.agentTeam,
        [],
        text,
        [MediaType.MAIL]
      )
      .then(success =>
        success.data.map((external: any) => ({
          id: external.uuid,
          firstname: capitalize(external.firstName),
          lastname: capitalize(external.lastName),
          name: `${external.lastName.toUpperCase()} ${external.firstName} (${external.email})`,
          directoryName: external.directory.name + (external.category && external.category.name ? `../${external.category.name}` : ''),
          phoneNumber: external.phoneNumber,
          email: external.email,
        }))
      );
  }

  searchContactById(): void {
    // check that input is not empty or too long before searching
    if (
      this.contactIdInput &&
      this.contactIdInput !== '' &&
      this.contactIdInput.length > 2 &&
      this.contactIdInput.length <= STRING_MAX_LENGTH_INPUT
    ) {
      this.outgoingMailService
        .searchContactById(this.currentPage, this.itemsPerPage, this.repositoryName)
        .then(totalItems => {
          this.contactNotFound = this.candidateContactList.length === 0;
          this.totalItems = totalItems;
        })
        .catch(() => {
          this.contactNotFound = true;
          this.$log.error(`   => Error while retrieving contact with id "${this.contactIdInput}"`);
        });
    } else if (this.candidateContactList) {
      this.candidateContactList.length = 0;
      this.$log.warn('Invalid empty or too long value for contact id input');
    }
  }

  pageChanged(): void {
    this.onRecipientTypeChange();
  }

  formatName(name: string): string {
    if (name === '' || name === ' ' || name == null) {
      return this.$i18next.t('agent:contact.info.unvalued_contact');
    }
    return name;
  }
}

export class MailOutgoingSelectionWorkspace implements IComponentOptions {
  templateUrl = templateUrl;
  bindings = {
    responseId: '<',
  };
  controller = [
    '$log',
    'contactDeprecatedService',
    'sessionDeprecatedService',
    'outgoingMailDeprecatedService',
    '$i18next',
    'agentHistory',
    'agentConfigDeprecatedService',
    'directory',
    'contactFacade',
    MailOutgoingSelectionWorkspaceCtrl,
  ];
}

export default 'contactMailOutgoingSelectionWorkspace';
